import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectUser,
  selectFeatureFlags,
  fetchFeatureFlags,
  selectHasAccess,
  shouldRequestFeatureFlags,
} from '@/store/features/userinfo/userinfoSlice';
import { getItemWithExpiry } from '@wavvest/shared-utils';
import { store } from '@/store';
import { setUser } from '@/store/features/userinfo/userinfoSlice';
import { getUserData } from '@/api/custom';
import { RouteLoadingFallback } from '@/routes/components/route-loading-fallback';

interface ProtectedRouteProps {
  children: React.ReactNode;
  featureFlag?: 'canvas_access' | 'advisor_prep_dashboard_access';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, featureFlag }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userFromStore = useAppSelector(selectUser);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const userFromLocalStorage = getItemWithExpiry('user');

  const hasAccess = useAppSelector((state) =>
    featureFlag ? selectHasAccess(state, featureFlag) : true,
  );

  useEffect(() => {
    if (shouldRequestFeatureFlags(userFromStore, featureFlags)) {
      dispatch(fetchFeatureFlags());
    }
  }, [userFromStore, dispatch, featureFlags]);

  useEffect(() => {
    // check and restore user state
    const token = getItemWithExpiry('access_token');
    const userStr = getItemWithExpiry('user');

    if (token && userStr) {
      try {
        const user = getUserData();
        store.dispatch(setUser(user));
      } catch {
        // getUserData already expires the session and redirects for invalid user data.
      }
    }
  }, []);

  const token = getItemWithExpiry('access_token');
  const user = getItemWithExpiry('user');

  if (!token || !user) {
    return <Navigate to="/" replace />;
  }

  if (!userFromStore && !userFromLocalStorage) {
    return <Navigate to={`/`} replace />;
  }

  let localStorageUser: Record<string, unknown> | null = null;
  if (userFromLocalStorage) {
    try {
      localStorageUser = JSON.parse(userFromLocalStorage) as Record<string, unknown>;
    } catch {
      localStorageUser = null;
    }
  }
  const activeUser = userFromStore ?? localStorageUser;
  if (activeUser?.incomplete_profile && window.location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  const featureAccessPending =
    !!featureFlag &&
    (featureFlags.loading || (featureFlags.profile === undefined && !featureFlags.error));

  if (featureAccessPending) {
    return <RouteLoadingFallback />;
  }

  if (featureFlags.error && featureFlag) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Failed to verify permissions</h2>
        <p>Failed to check feature permissions. Please try again later.</p>
        <button onClick={() => navigate('/home')}>Back to Home</button>
      </div>
    );
  }

  if (featureFlag && !hasAccess) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Feature not enabled</h2>
        <p>
          Your account does not have access to this feature. Please contact your administrator or
          upgrade your plan.
        </p>
        <button onClick={() => navigate('/home')}>Back to Home</button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
